import { cn } from '@/lib/utils';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Button, Icon, TextArea, TextField } from '@pms-digital/design-system';
import { useEffect, useRef } from 'react';
import { type Control, Controller, useWatch } from 'react-hook-form';
import type { DimensionsFormValues } from './dimensions-step';

interface DimensionRowProps {
  sortableId: string;
  index: number;
  control: Control<DimensionsFormValues>;
  isExpanded: boolean;
  /** True only for the row `handleAdd` just appended — focuses the title field once, on mount of the expanded state. */
  autoFocusTitle: boolean;
  /** An archived company: no reordering, removal or editing. */
  readOnly: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onFieldChange: () => void;
}

/**
 * One draggable row of the dimensions step. Collapsed to a single line
 * (title + description preview + a warning dot if the description is
 * missing) until clicked, then expands to the two editable fields — title
 * (blocking) + description (warning-only, never blocks Suivant).
 */
export function DimensionRow({
  sortableId,
  index,
  control,
  isExpanded,
  autoFocusTitle,
  readOnly,
  onToggleExpand,
  onRemove,
  onFieldChange,
}: DimensionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });

  const title = useWatch({ control, name: `dimensions.${index}.title` });
  const description = useWatch({ control, name: `dimensions.${index}.description` });

  const titleInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocusTitle) titleInputRef.current?.focus();
  }, [autoFocusTitle]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // A row that has content but no title would be silently dropped on save —
  // flag it as blocking (danger) rather than let that happen invisibly. The
  // pristine default empty row (both fields blank) shows no error.
  const showTitleError = title.trim().length === 0 && description.trim().length > 0;
  // A titled row with no description is valid (no minimum), just advisory —
  // surfaced as a small dot on the collapsed line, never blocking Suivant.
  const showDescriptionWarning = title.trim().length > 0 && description.trim().length === 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-row-index={index}
      className={cn('rounded-lg border border-gray-200 bg-white', isDragging && 'opacity-50')}
    >
      <div className="flex items-center gap-3 p-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={readOnly}
          aria-label={`Réordonner la dimension ${index + 1}`}
          className="flex h-9 w-9 shrink-0 cursor-grab items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 active:cursor-grabbing"
        >
          <Icon name="drag_indicator" size={16} />
        </button>

        <button
          type="button"
          onClick={onToggleExpand}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={isExpanded}
        >
          <span className="truncate font-semibold text-small">
            {title.trim() || <span className="text-gray-400">Dimension sans titre</span>}
          </span>
          {description.trim() && (
            <span className="truncate text-gray-500 text-small">— {description.trim()}</span>
          )}
          {showDescriptionWarning && (
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning"
              aria-label="Description recommandée"
              title="Une description est recommandée pour cette dimension."
            />
          )}
        </button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={readOnly}
          aria-label={`Supprimer la dimension ${index + 1}`}
        >
          <Icon name="delete" size={16} />
        </Button>
      </div>

      {isExpanded && (
        <div className="space-y-2 border-gray-100 border-t p-3">
          <Controller
            control={control}
            name={`dimensions.${index}.title`}
            render={({ field }) => (
              <TextField
                label={<span className="sr-only">{`Titre de la dimension ${index + 1}`}</span>}
                {...field}
                ref={(element) => {
                  field.ref(element);
                  titleInputRef.current = element;
                }}
                readOnly={readOnly}
                placeholder="Titre de la dimension"
                status={showTitleError ? 'error' : 'default'}
                onChange={(event) => {
                  field.onChange(event);
                  onFieldChange();
                }}
              />
            )}
          />
          {showTitleError && (
            <Alert tone="danger">
              Le titre est requis, sinon cette dimension ne sera pas enregistrée.
            </Alert>
          )}

          <Controller
            control={control}
            name={`dimensions.${index}.description`}
            render={({ field }) => (
              <TextArea
                label={
                  <span className="sr-only">{`Description de la dimension ${index + 1}`}</span>
                }
                {...field}
                readOnly={readOnly}
                placeholder="Description (optionnelle)"
                onChange={(event) => {
                  field.onChange(event);
                  onFieldChange();
                }}
              />
            )}
          />
          {showDescriptionWarning && (
            <Alert tone="warning">Une description est recommandée pour cette dimension.</Alert>
          )}
        </div>
      )}
    </div>
  );
}
